// Canonical sharing-UI template — shared types.
// Mirrors the StructuraMatrix.SharingModule HTTP contract (see its README).
// This file is synced into each app by sync-sharing-ui.sh; do not edit per-app —
// edit the canonical copy in ToolsForClaudia/sharing-ui/ and re-sync.

export type SharingRole = number; // 0 Viewer (v1); 1 Editor / 2 Owner reserved

export interface ShareGrant {
  id: string;
  resourceType: string;
  resourceId: string;
  granteeUserId: string;
  granteeEmail: string;
  granteeName: string;
  role: SharingRole;
  createdAt: string;
}

export interface ShareLinkInfo {
  token: string;
  resourceType: string;
  resourceId: string;
  label: string | null;
  role: SharingRole;
  createdAt: string;
  expiresAt: string;
  url: string;
}

export interface SharedWithMeItem {
  shareId: string;
  resourceType: string;
  title: string;
  subtitle: string | null;
  resourceCreatedAt: string;
  ownerEmail: string;
  ownerName: string;
  sharedAt: string;
}

/** Uniform artifact keys across every row resource (each app's result JSON maps to outputJson). */
export interface SharedArtifact {
  inputJson: string;
  outputJson: string;
}

export interface SharedMeta {
  title: string;
  subtitle: string | null;
  createdAtUtc: string;
}

export interface SharedViewResponse {
  share: {
    id: string;
    resourceType: string;
    resourceId: string;
    role: SharingRole;
    ownerEmail: string;
    ownerName: string;
    sharedAt: string;
  };
  meta: SharedMeta;
  artifact: SharedArtifact;
}

export interface LinkViewResponse {
  resourceType: string;
  meta: SharedMeta;
  artifact: SharedArtifact;
}

/** Carries the HTTP status so viewers can tell 404 (invalid) from 410 (expired/removed). */
export class SharingApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "SharingApiError";
  }
}

/** The only per-app wiring the client needs: where the API is and how to read the token. */
export interface SharingConfig {
  /** Absolute API base, e.g. "http://localhost:5188/api" or a same-origin "/api". */
  apiBase: string;
  /** Returns the current bearer token, or null when signed out. */
  getToken: () => string | null;
}

export interface SharingClient {
  listShares(resourceType: string, resourceId: string): Promise<ShareGrant[]>;
  addShare(resourceType: string, resourceId: string, granteeEmail: string): Promise<ShareGrant>;
  revokeShare(resourceType: string, resourceId: string, granteeUserId: string): Promise<void>;
  listLinks(resourceType: string, resourceId: string): Promise<ShareLinkInfo[]>;
  createLink(resourceType: string, resourceId: string, expiryDays: number, label: string): Promise<ShareLinkInfo>;
  revokeLink(resourceType: string, resourceId: string, token: string): Promise<void>;
  listSharedWithMe(): Promise<SharedWithMeItem[]>;
  getShared(shareId: string): Promise<SharedViewResponse>;
  getLink(token: string): Promise<LinkViewResponse>;
}
